import React, { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "../lib/supabase";
import { Loader as Loader2, Image as ImageIcon, Share2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, animate, useMotionValue } from "framer-motion";

// --- CONFIG ---
const API_URL = "https://bald-backend.onrender.com/url";
const BUCKET = "images";

// --- TYPES ---
type SiteAnalysis = {
  left_hairline?: number;
  right_hairline?: number;
  crown?: number;
  thickness?: number;
};

type Result = {
  bald_prediction: string | null;
  problem_site: string | null;
  site_analysis: SiteAnalysis | null;
  raw_response: string | null;
  grade?: number | null;
};

// --- COLOR UTILS ---
function scoreColor(score: number | null | undefined): string {
  if (score == null || isNaN(score)) return "text-gray-400";
  const value = score / 10;
  if (value < 6) return "text-red-500";
  if (value < 7.5) return "text-orange-400";
  if (value < 9) return "text-yellow-400";
  return "text-green-500";
}

// --- ANIMATED NUMBER ---
const AnimatedNumber: React.FC<{
  value?: number | null;
  duration?: number;
  suffix?: string;
}> = ({ value = 0, duration = 2.6, suffix = "" }) => {
  const start = 20;
  const end = value ?? 0;
  const motionValue = useMotionValue(start);
  const [display, setDisplay] = useState(start);

  useEffect(() => {
    const controls = animate(motionValue, end, {
      duration,
      ease: [0.4, 0, 0.2, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return controls.stop;
  }, [end, duration, motionValue]);

  return (
    <span>
      {display}
      {suffix}
    </span>
  );
};

// --- SCORE CARD ---
const ScoreCard: React.FC<{
  label: string;
  value?: number | null;
  microcopy?: string;
}> = ({ label, value, microcopy }) => {
  const outOfTen =
    value !== null && value !== undefined && !Number.isNaN(value)
      ? (value / 10).toFixed(1)
      : null;
  const colorClass = scoreColor(value);

  return (
    <div className="flex flex-col items-center justify-center rounded-xl shadow-md p-2 sm:p-4 bg-gray-900/70 backdrop-blur-sm border border-gray-800 min-h-[74px]">
      <div className="text-xs sm:text-sm font-medium opacity-80 text-gray-100">{label}</div>
      <div className={`mt-1 text-2xl sm:text-3xl font-extrabold leading-none ${colorClass}`}>
        {outOfTen !== null ? `${outOfTen}/10` : "-/10"}
      </div>
      {microcopy && (
        <div className="text-[11px] mt-1 text-gray-400 font-normal text-center leading-tight">
          {microcopy}
        </div>
      )}
    </div>
  );
};

// --- CTA LABELS BASED ON GRADE ---
const CTA_LABELS = [
  "Stop Hair Loss Now",           // grade 1
  "Take Control of Hair Loss",    // grade 2
  "Regrow your Hair Loss"         // grade 3
];

// --- STEP 2 ---
type Step2Props = {
  baldYears: number | null;
  localUrl: string | null;
  result: Result | null;
  reveal: boolean;
  navigate: ReturnType<typeof useNavigate>;
  minHaircuts: number | null;
  maxHaircuts: number | null;
};

const Step2: React.FC<Step2Props> = ({
  baldYears,
  localUrl,
  result,
  reveal,
  navigate,
  minHaircuts,
  maxHaircuts,
}) => {
  // Use grade **only** for CTA
  const grade = typeof result?.grade === "number" ? result.grade : null;

  // --- Sharing ---
  const handleShare = async () => {
    if (!result) return;

    let shareText = `My hair loss analysis:\n`;
    if (result.bald_prediction) shareText += `• Estimated years until bald: ${result.bald_prediction}\n`;
    if (result.problem_site) shareText += `• Problem site: ${result.problem_site}\n`;

    if (result.site_analysis) {
      shareText += `• Scores:\n`;
      shareText += Object.entries(result.site_analysis)
        .map(([k, v]) => {
          const nice = k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
          const outOfTen =
            v !== null && v !== undefined && !Number.isNaN(v)
              ? `${(v / 10).toFixed(1)}/10`
              : "-/10";
          return `   - ${nice}: ${outOfTen}`;
        })
        .join("\n");
      shareText += "\n";
    }
    shareText += "\nCheck yours at WhenBald.com/start";

    try {
      if (navigator.share) {
        await navigator.share({ title: "My Hair Loss Results", text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
        alert("Share text copied! Paste it anywhere to share your results.");
      }
    } catch {
      // user cancelled share — do nothing
    }
  };

  // --- Dynamic CTA for grade (defaults to 2 if null/invalid) ---
  const getCTALabel = () => {
    if (grade && grade >= 1 && grade <= 3) {
      return CTA_LABELS[grade - 1];
    }
    return CTA_LABELS[1]; // fallback to "Take Control of Hair Loss"
  };

  return (
    <section className="w-full px-4 pb-12 flex flex-col items-center">
      <motion.h1
        className="text-3xl sm:text-4xl font-bold text-gray-900 mt-6 text-center tracking-tight mb-4"
        initial={{ opacity: 0, y: 26 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
      >
        <>
          Bald in{" "}
          <span className="inline-block text-primary-700">
            {typeof baldYears === "number" ? <AnimatedNumber value={baldYears} /> : <span>-</span>}
          </span>{" "}
          years
        </>
      </motion.h1>

      <div className="relative w-full max-w-sm mx-auto flex items-center justify-center mb-8">
        {localUrl && (
          <img
            src={localUrl}
            alt="Result"
            className="rounded-2xl border border-gray-300 shadow-lg w-full object-cover max-h-72 min-h-[220px]"
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="grid grid-cols-2 grid-rows-2 gap-3 w-[92%] h-[82%]">
            <ScoreCard label="Crown" value={result?.site_analysis?.crown} />
            <ScoreCard label="Thickness" value={result?.site_analysis?.thickness} />
            <ScoreCard
              label="Left Hairline"
              value={result?.site_analysis?.left_hairline}
              microcopy="(Left of image)"
            />
            <ScoreCard
              label="Right Hairline"
              value={result?.site_analysis?.right_hairline}
              microcopy="(Right of image)"
            />
          </div>
        </div>
      </div>

      <motion.p
        className="text-gray-700 text-center font-medium max-w-md mb-6"
        initial={{ opacity: 0, filter: "blur(8px)" }}
        animate={reveal ? { opacity: 1, filter: "blur(0px)" } : { opacity: 0.55, filter: "blur(8px)" }}
        transition={{ duration: 1.1, ease: "easeInOut" }}
      >
        {result?.problem_site ||
          "No specific problem areas detected. If you're concerned about your hair, consult a medical professional."}
      </motion.p>

      {/* Dynamic CTA with microcopy */}
      <div className="flex flex-col w-full items-center gap-1 mb-3">
        <button
          onClick={() => {
            if (grade) {
              navigate(`/product?grade=${grade}`);
            } else {
              navigate("/product");
            }
          }}
          type="button"
          className="flex items-center gap-2 px-4 py-3 rounded-xl bg-green-200 border border-green-600 text-black font-semibold shadow hover:bg-green-300 transition text-lg"
        >
          {getCTALabel()}
          <ArrowRight className="w-5 h-5" />
        </button>
        <span className="block text-xs text-gray-400 font-medium mt-1">
          Personalized hair loss routine for you
        </span>
      </div>

      {/* Share button */}
      <div className="flex flex-row w-full justify-center gap-3 mb-6">
        <button
          onClick={handleShare}
          type="button"
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-300 text-gray-700 font-semibold shadow hover:bg-gray-100 transition"
        >
          <Share2 className="w-4 h-4"/>
          Share
        </button>
      </div>
    </section>
  );
};

// --- MAIN PAGE ---
const Start: React.FC = () => {
  const [step, setStep] = useState<1 | 2>(1);
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [reveal, setReveal] = useState(false);

  const navigate = useNavigate();

  const baldYears = useMemo(() => {
    if (!result?.bald_prediction) return null;
    const match = result.bald_prediction.match(/(\d+)/);
    const years = parseInt(match?.[1] ?? "0", 10);
    return Number.isFinite(years) ? years : null;
  }, [result?.bald_prediction]);

  const minHaircuts = baldYears ? baldYears * 12 : null;
  const maxHaircuts = baldYears ? baldYears * 36 : null;

  const resetForNewUpload = () => {
    setError(null);
    setReveal(false);
    setResult(null);
  };

  const handleImageInput = async (file: File | Blob) => {
    resetForNewUpload();

    try {
      setLocalUrl(URL.createObjectURL(file as File));
    } catch {
      setLocalUrl(null);
    }

    setUploading(true);

    try {
      const filename = `hair_${uuidv4()}.jpg`;

      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(filename, file, { contentType: "image/jpeg" });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from(BUCKET).getPublicUrl(filename);
      const publicUrl = data?.publicUrl;

      if (!publicUrl) throw new Error("No public URL returned.");

      await analyzePhoto(publicUrl);
    } catch {
      setError("Image upload failed. Please try another photo.");
      setLocalUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const analyzePhoto = async (imageUrl: string) => {
    setAnalyzing(true);

    try {
      const resp = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_url: imageUrl }),
      });

      const data = await resp.json();

      // Log the formatted AI API response for debugging/inspection
      console.log("[AI API response]", {
        bald_prediction: data?.bald_prediction ?? null,
        problem_site: data?.problem_site ?? null,
        site_analysis: data?.site_analysis ?? null,
        raw_response: data?.raw_response ?? null,
        grade: typeof data?.grade === "number" ? data.grade : null,
      });

      setResult({
        bald_prediction: data?.bald_prediction ?? null,
        problem_site: data?.problem_site ?? null,
        site_analysis: data?.site_analysis ?? null,
        raw_response: data?.raw_response ?? null,
        grade: typeof data?.grade === "number" ? data.grade : null,
      });

      setStep(2);
      setTimeout(() => setReveal(true), 1500);
    } catch {
      setError("Analysis failed. Please try another image.");
      setStep(1);
    } finally {
      setAnalyzing(false);
    }
  };

  const Step1 = (
    <section className="w-full flex flex-col items-center gap-8 px-4 py-16">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-700">Your Hair Analysis</h1>

      <p className="text-gray-600 text-center max-w-lg">
        Upload a clear photo of your hairline and crown to see your hair loss scores and risk of balding.
      </p>

      {!localUrl && !uploading && !analyzing && (
        <label className="cursor-pointer py-12 px-8 border-2 border-dashed border-primary-500 rounded-2xl hover:bg-red-50 flex flex-col items-center gap-3 w-full max-w-sm">
          <ImageIcon size={36} className="text-gray-400" />
          <span className="font-medium">Tap here to upload/take your photo</span>
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={(e) => e.target.files && handleImageInput(e.target.files[0])}
          />
        </label>
      )}

      {localUrl && (
        <div className="relative flex flex-col items-center">
          <img
            src={localUrl}
            alt="Preview"
            className={`rounded-xl border max-h-52 shadow transition ${uploading || analyzing ? "opacity-95 blur-sm" : ""}`}
          />
          {(uploading || analyzing) && (
            <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/40 backdrop-blur-sm">
              <Loader2 className="animate-spin text-primary-600" size={42} />
            </div>
          )}
          <span className="flex items-center gap-1 justify-center text-xs text-gray-300 mt-2 text-center">
            GDPR Secure &amp; Confidential
            <br />
            Not medical advice. Results are educational.
          </span>
        </div>
      )}

      {!localUrl && (
        <span className="flex items-center gap-1 justify-center text-xs text-gray-300 mt-8 text-center">
          GDPR Secure &amp; Confidential
          <br />
          Not medical advice. Results are educational.
        </span>
      )}

      {error && <p className="text-red-600 font-semibold">{error}</p>}
    </section>
  );

  return (
    <main className="w-full min-h-[calc(100vh-56px)] bg-gradient-to-b from-red-50 to-white">
      {step === 1 ? (
        Step1
      ) : result ? (
        <Step2
          baldYears={baldYears}
          localUrl={localUrl}
          result={result}
          reveal={reveal}
          navigate={navigate}
          minHaircuts={minHaircuts}
          maxHaircuts={maxHaircuts}
        />
      ) : (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="animate-spin text-primary-600" size={42} />
        </div>
      )}
    </main>
  );
};

export default Start;
